import { loadSelectedSchool } from 'tio-common/utils/tuition-assistance/application-new-route';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';

/**
 * Whether the LPN discount survey belongs on a post-submission surface.
 *
 * Extracted from the confirmation route so the manage screen's submitted modal
 * decides this identically. The guided flow now lands on `/manage` rather than
 * `/confirmation`, and the survey follows it there — two copies of these rules
 * would eventually disagree about which employees get asked.
 */

/**
 * Builds the LPN institution identifier (e.g. `dapip_123` / `tas_456`) the same
 * way the API does, so it can be matched against a company's LPN settings.
 */
function lpnIdentifierFor(programInstance: TasProgramInstanceModel): string | undefined {
  const dapipId = programInstance.fields['DAPIP_INSTITUTION_ID']?.values?.[0];
  if (dapipId) {
    return `dapip_${dapipId}`;
  }
  const tasId = programInstance.fields['TAS_INSTITUTION_ID']?.values?.[0];
  if (tasId) {
    return `tas_${tasId}`;
  }
  return undefined;
}

/**
 * The LPN discount survey is shown only when the application's school is in the
 * Learning Partner Network AND the company has LPN enabled — unless the company
 * has explicitly disabled that specific school (opt-out).
 *
 * Costs an institution lookup, so callers should only ask when they are actually
 * about to render a post-submission surface.
 */
export async function shouldShowLpnSurvey(
  application: TasApplicationModel,
  { store, sessionContext }: { store: Store; sessionContext: SessionContextService }
): Promise<boolean> {
  const programInstance = application.tasProgramInstance;
  if (!programInstance) {
    return false;
  }

  // Company-wide LPN toggle must be on.
  const companySetting = sessionContext.currentEmployee?.company?.companySetting;
  if (!companySetting?.lpn?.isEnabled) {
    return false;
  }

  // Confirm the school is in the LPN network via its unified-institution record.
  // loadSelectedSchool includes the program-template filter, which the `lpn`
  // flag depends on.
  const institution = await loadSelectedSchool(programInstance, store);
  if (!institution?.lpn) {
    return false;
  }

  // Opt-out: show unless the company has an explicit, disabled setting for this
  // school. A confirmed LPN school always has an identifier (loadSelectedSchool
  // returns undefined otherwise), but guard on it so a missing identifier can
  // never accidentally match a settings row.
  // Read, don't await: `companyLpnSettings` is a sync hasMany, so this resolves
  // from whatever the cache holds and can never attempt a fetch. It used to
  // `await companySetting.companyLpnSetting` — singular, which never matched the
  // plural name the API sends, so the relationship was always empty AND the
  // await could throw "No adapter was found for 'company-lpn-setting'" and take
  // down the route. Both fixed in tio-data; this reads the corrected field.
  const identifier = lpnIdentifierFor(programInstance);
  const settings = companySetting.companyLpnSettings;
  const explicitlyDisabled = Boolean(
    identifier &&
    settings?.some(
      (setting) =>
        setting.lpnPage?.institutionIdentifier === identifier && setting.isEnabled === false
    )
  );
  return !explicitlyDisabled;
}
